// =============================================================================
// FILE: packages/renderer/src/components/context/MasterContextIngestor.tsx
// PURPOSE: "Master Context Ingestor" UX. Paste/load project trade data → R²
//   scans it (cinematic), presents a multi-trade Strategy Summary, and on
//   approval generates a certified Multi-Trade Design Package PDF.
// =============================================================================

import { useCallback, useRef, useState, type JSX } from 'react';
import {
  ScanSearch, Loader2, CheckCircle2, Download, X, Sparkles, Clipboard, Layers,
} from 'lucide-react';
import { useLicense } from '../../hooks/useLicense.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { isDemoMode, SIERRA_MANOR_PROJECT_NAME } from '../../config/demoMode.js';
import { growthAgent } from '../../stores/growthAgent.store.js';
import { analyzeMultiTrade, SAMPLE_PROJECT, type MultiTradeResult } from '../../lib/contextAnalyzer.js';
import { generateDesignPackage, type PackageResult } from '../../lib/contextPackageGenerator.js';

type Phase = 'idle' | 'input' | 'scanning' | 'summary' | 'generating' | 'ready';

const STAGES = [
  'Reading trade coordination export',
  'Discovering buildings, floors & rooms',
  'Detecting active engineering trades',
  'Cross-referencing ANSI/TIA · NFPA · NEC/CEC',
  'Sizing systems & estimating fees',
  'Composing strategy summary',
];

export function MasterContextIngestor(): JSX.Element {
  const { licenseStatus } = useLicense();
  const user = useAuthStore((s) => s.user);
  const isDesigner = licenseStatus?.tier === 'designer';
  const accent = isDesigner ? '#fcd34d' : '#25B5A8';

  const [phase, setPhase] = useState<Phase>('idle');
  const [text, setText] = useState('');
  const [stageIdx, setStageIdx] = useState(0);
  const [result, setResult] = useState<MultiTradeResult | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [pkg, setPkg] = useState<PackageResult | null>(null);
  const runningRef = useRef(false);

  const projectName = isDemoMode() ? SIERRA_MANOR_PROJECT_NAME : 'Active Portfolio';
  const authorName = (() => {
    const name = user ? ([user.firstName, user.lastName].filter(Boolean).join(' ') || user.email?.split('@')[0] || 'Engineer') : 'Engineer';
    if (licenseStatus?.bicsiId && licenseStatus.bicsiId !== 'dev-preview') {
      const id = licenseStatus.bicsiId === 'MASTER-ADMIN' ? 'MASTER' : licenseStatus.bicsiId.replace(/^#/, '');
      return `${name}, RCDD #${id}`;
    }
    return name;
  })();

  const scan = useCallback(async () => {
    if (runningRef.current || !text.trim()) return;
    runningRef.current = true;
    setPhase('scanning');
    setStageIdx(0);
    for (let i = 0; i < STAGES.length; i++) {
      setStageIdx(i);
      await new Promise((r) => setTimeout(r, 420));
    }
    setResult(analyzeMultiTrade(text));
    setPhase('summary');
    runningRef.current = false;
  }, [text]);

  const generate = useCallback(async () => {
    if (!result) return;
    setPhase('generating');
    setPreview(null);
    const res = await generateDesignPackage(result, projectName, authorName, (dataUrl, i) => { if (i === 0) setPreview(dataUrl); });
    setPkg(res);
    setPreview(res.firstPageDataUrl);
    setPhase('ready');
    growthAgent.celebrate('generic_win');
  }, [result, projectName, authorName]);

  const open = useCallback(() => { setText(''); setResult(null); setPkg(null); setPreview(null); setPhase('input'); }, []);
  const close = useCallback(() => {
    if (pkg) URL.revokeObjectURL(pkg.blobUrl);
    setPhase('idle'); setResult(null); setPkg(null); setPreview(null);
  }, [pkg]);
  const download = useCallback(() => {
    if (!pkg) return;
    const a = document.createElement('a'); a.href = pkg.blobUrl; a.download = pkg.fileName; a.click();
  }, [pkg]);

  return (
    <>
      <style>{`
        @keyframes mci-flyin { 0%{opacity:0;transform:translateY(20px) scale(0.96)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes mci-scan { 0%{transform:translateY(0)} 100%{transform:translateY(300px)} }
      `}</style>

      {/* Trigger card */}
      <button
        type="button"
        data-testid="master-context-btn"
        onClick={open}
        className="group relative w-full overflow-hidden rounded-xl px-6 py-5 text-left transition-transform hover:scale-[1.005]"
        style={{
          background: 'linear-gradient(135deg, rgba(129,140,248,0.14), rgba(37,181,168,0.08))',
          border: `1.5px solid ${accent}`,
          boxShadow: `0 0 0 1px ${accent}22, 0 10px 40px ${accent}1f`,
        }}
      >
        <div className="relative flex items-center gap-4">
          <div className="grid place-items-center rounded-lg shrink-0" style={{ width: 52, height: 52, background: `${accent}1f`, border: `1px solid ${accent}55` }}>
            <ScanSearch className="h-6 w-6" style={{ color: accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: `${accent}cc` }}>Master Architect · Autonomous Design</div>
            <div className="text-lg font-extrabold tracking-tight text-zinc-50 leading-tight mt-0.5">MASTER CONTEXT INGESTOR</div>
            <div className="text-[12px] text-zinc-400 mt-0.5">Scan project trade data → R² proposes a coordinated multi-trade design package</div>
          </div>
          <Layers className="h-5 w-5 shrink-0" style={{ color: accent }} />
        </div>
      </button>

      {phase !== 'idle' && (
        <div data-testid="mci-overlay" className="fixed inset-0 z-[10000] grid place-items-center p-6" style={{ background: 'rgba(4,7,14,0.82)', backdropFilter: 'blur(6px)' }}>
          <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden" style={{ background: 'rgba(9,14,24,0.98)', border: `1.5px solid ${accent}55`, boxShadow: `0 30px 120px rgba(0,0,0,0.7), 0 0 60px ${accent}22`, maxHeight: '90vh' }}>
            {/* header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${accent}22` }}>
              <div className="flex items-center gap-3">
                <ScanSearch className="h-5 w-5" style={{ color: accent }} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: `${accent}cc` }}>RCD² Master Context Ingestor</div>
                  <div className="text-sm font-bold text-zinc-100">
                    {phase === 'input' ? 'Ingest Project Data' : phase === 'scanning' ? 'Analyzing Project' : phase === 'summary' ? 'Strategy Summary' : phase === 'generating' ? 'Generating Package' : 'Design Package Ready'}
                  </div>
                </div>
              </div>
              <button type="button" data-testid="mci-close-btn" onClick={close} disabled={phase === 'scanning' || phase === 'generating'}
                className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200 transition" style={{ border: '1px solid rgba(63,63,70,0.6)' }}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 66px)' }}>
              {/* INPUT */}
              {phase === 'input' && (
                <div className="p-6">
                  <p className="text-[13px] text-zinc-400 mb-3">Paste a trade coordination export, Revit schedule, or requirements narrative. R² will discover every trade and propose a coordinated design package.</p>
                  <textarea
                    data-testid="mci-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste project data here…"
                    className="w-full h-56 rounded-lg bg-zinc-950/70 border border-zinc-700 p-3 text-[12px] font-mono text-zinc-200 focus:outline-none focus:border-teal-500 resize-none"
                  />
                  <div className="flex items-center justify-between mt-4">
                    <button type="button" data-testid="mci-load-sample" onClick={() => setText(SAMPLE_PROJECT)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] text-zinc-300 border border-zinc-700 hover:border-teal-500 hover:text-teal-200 transition">
                      <Clipboard className="h-4 w-4" /> Load Sample (Sierra Manor)
                    </button>
                    <button type="button" data-testid="mci-scan-btn" onClick={scan} disabled={!text.trim()}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-[12px] uppercase tracking-[0.14em] transition disabled:opacity-40"
                      style={{ background: accent, color: '#0a0f1a', boxShadow: `0 8px 30px ${accent}44` }}>
                      <ScanSearch className="h-4 w-4" /> Scan & Analyze
                    </button>
                  </div>
                </div>
              )}

              {/* SCANNING */}
              {phase === 'scanning' && (
                <div className="p-8">
                  <ul className="space-y-3" data-testid="mci-scan-stages">
                    {STAGES.map((s, i) => {
                      const done = i < stageIdx; const active = i === stageIdx;
                      return (
                        <li key={s} className="flex items-center gap-3">
                          {done ? <CheckCircle2 className="h-4 w-4" style={{ color: accent }} />
                            : active ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: accent }} />
                            : <span className="block h-4 w-4 rounded-full border" style={{ borderColor: 'rgba(100,116,139,0.5)' }} />}
                          <span className="text-[13px]" style={{ color: done || active ? 'rgb(226,232,240)' : 'rgba(100,116,139,0.7)' }}>{s}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* SUMMARY */}
              {phase === 'summary' && result && (
                <div className="p-6" data-testid="mci-strategy">
                  <div className="flex items-start gap-3 rounded-lg p-4 mb-5" style={{ background: `${accent}12`, border: `1px solid ${accent}33` }}>
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
                    <p className="text-[13px] text-zinc-200 leading-relaxed m-0">
                      <span className="font-semibold" style={{ color: accent }}>R²:</span> {result.strategy}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {result.packages.filter((p) => p.detected).map((p) => (
                      <div key={p.key} data-testid={`mci-trade-${p.key}`} className="rounded-lg p-4" style={{ background: 'rgba(14,20,38,0.8)', border: `1px solid ${p.color}55` }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[13px] font-bold" style={{ color: p.color }}>{p.label}</span>
                          <span className="text-[13px] font-mono font-bold text-zinc-100">${p.estimateUsd.toLocaleString()}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">{p.division}</div>
                        <ul className="space-y-1">
                          {p.items.map((it, ix) => (
                            <li key={ix} className="text-[11px] text-zinc-400 flex gap-2"><span style={{ color: p.color }}>•</span>{it}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between rounded-lg p-4 mb-5" style={{ background: 'rgba(37,181,168,0.08)', border: `1px solid ${accent}44` }}>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Preliminary Design Fee</div>
                      <div className="text-2xl font-extrabold" style={{ color: accent }} data-testid="mci-total">${result.totalUsd.toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Est. Delivery</div>
                      <div className="text-2xl font-extrabold text-amber-300">~{result.timelineWeeks} wks</div>
                    </div>
                  </div>

                  <button type="button" data-testid="mci-generate-btn" onClick={generate}
                    className="w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.14em] transition-transform hover:scale-[1.01]"
                    style={{ background: accent, color: '#0a0f1a', boxShadow: `0 8px 30px ${accent}55` }}>
                    <Sparkles className="h-4 w-4" /> Approve & Generate Design Package
                  </button>
                </div>
              )}

              {/* GENERATING / READY */}
              {(phase === 'generating' || phase === 'ready') && (
                <div className="p-6 flex flex-col items-center">
                  <div className="relative rounded-lg overflow-hidden w-full max-w-2xl" style={{ aspectRatio: '1754 / 1240', border: `1px solid ${accent}33`, background: '#070b14' }}>
                    {preview ? (
                      <img src={preview} alt="Design package preview" data-testid="mci-preview-img" className="w-full h-full object-cover" style={{ animation: 'mci-flyin 0.5s cubic-bezier(0.2,0.8,0.2,1)' }} />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center text-zinc-600 text-[11px] uppercase tracking-widest">Composing cover…</div>
                    )}
                    {phase === 'generating' && (
                      <div aria-hidden style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, boxShadow: `0 0 18px ${accent}`, animation: 'mci-scan 1.6s ease-in-out infinite alternate' }} />
                    )}
                  </div>
                  {phase === 'ready' && (
                    <button type="button" data-testid="mci-download-btn" onClick={download}
                      className="mt-5 w-full max-w-2xl flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.14em] transition-transform hover:scale-[1.01]"
                      style={{ background: accent, color: '#0a0f1a', boxShadow: `0 8px 30px ${accent}55` }}>
                      <Download className="h-4 w-4" /> Download Multi-Trade Package (PDF)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
